import type {Signal, WritableSignal} from "@angular/core";
import {linkedSignal, Service, signal, untracked} from "@angular/core";
import {equals, mapEquals} from "../helpers/Equatable";
import type {IPaginationParameters} from "../models/base/PaginationParameters";
import {areEqualPaginationParameters} from "../models/base/PaginationParameters";
import type {SortParameterLists} from "../models/base/SortParameters";
import type {TableFilterStorage} from "../models/base/table/TableFilterStorage";

@Service()
export class TableParametersStorage {
  private readonly filterStorageByTableId = signal<ReadonlyMap<string, TableFilterStorage<object>>>(
    new Map(), {equal: mapEquals});
  private readonly sortParametersByTableId = signal<ReadonlyMap<string, SortParameterLists<object>>>(
    new Map(), {equal: mapEquals});
  private readonly paginationParametersByTableId = signal<ReadonlyMap<string, IPaginationParameters>>(
    new Map(), {equal: (a, b) => mapEquals(a, b, areEqualPaginationParameters)});

  private getStorageForTable<TValue>(
    tableId: Signal<string>,
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    storageByTableId: WritableSignal<ReadonlyMap<string, TValue>>,
    initialValue: Signal<TValue>,
    equalityFn: (lhs: TValue, rhs: TValue) => boolean,
  ): WritableSignal<TValue> {
    return linkedSignal(
      () => storageByTableId().get(tableId()) ?? initialValue(),
      {
        set: (newValue, rawSet) => {
          storageByTableId.update(current => {
            const tableIdValue = untracked(tableId);
            if (equals(newValue, current.get(tableIdValue), equalityFn)) return current;
            return new Map(current).set(tableIdValue, newValue);
          });
          rawSet(newValue);
        },
        equal: equalityFn,
      });
  }

  public getFilterStorageForTable<TModel extends object>(
    tableId: Signal<string>,
    initialStorage: Signal<TableFilterStorage<TModel>>,
  ): WritableSignal<TableFilterStorage<TModel>> {
    return this.getStorageForTable(tableId, this.filterStorageByTableId, initialStorage, equals);
  }

  public getSortParametersForTable<TModel extends object>(
    tableId: Signal<string>,
    initialParameters: Signal<SortParameterLists<TModel>>,
  ): WritableSignal<SortParameterLists<TModel>> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return this.getStorageForTable(tableId, this.sortParametersByTableId, initialParameters, equals) as WritableSignal<SortParameterLists<TModel>>;
  }

  public getPaginationParametersForTable(
    tableId: Signal<string>,
    initialParameters: Signal<IPaginationParameters>,
  ): WritableSignal<IPaginationParameters> {
    return this.getStorageForTable(tableId, this.paginationParametersByTableId, initialParameters, areEqualPaginationParameters);
  }

  public clearAllTableParameters(): void {
    this.filterStorageByTableId.set(new Map());
    this.sortParametersByTableId.set(new Map());
    this.paginationParametersByTableId.set(new Map());
  }
}
